import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function SubmitScore({ currentBar, onScoreSubmitted, open, onClose }) {
  const { user, isAuthenticated } = useAuth();
  const [score, setScore] = useState('');
  const [existingScore, setExistingScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (currentBar && user) {
      // Check if user already has a score for this bar
      const userScore = currentBar.scores?.find(
        s => s.user_id === user.id || s.name === user.name
      );
      if (userScore) {
        setExistingScore(userScore);
        setScore(userScore.score.toString());
      } else {
        setExistingScore(null);
        // Default to par score
        setScore(currentBar.par ? currentBar.par.toString() : '');
      }
      setError(null);
      setSuccess(false);
    }
  }, [currentBar, user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !user) {
      setError('Please sign in to submit a score');
      return;
    }

    const scoreValue = parseInt(score);
    if (isNaN(scoreValue) || scoreValue < 1) {
      setError('Please enter a valid score (1 or higher)');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const scoreData = {
        name: user.name,
        score: scoreValue,
        avatar: user.picture,
      };

      if (existingScore) {
        // Update existing score - we'll need to find the index
        // For now, we'll use a PUT endpoint that finds by user_id
        await axios.put(`${API_URL}/api/bars/${currentBar.id}/scores/user`, scoreData);
      } else {
        // Add new score
        await axios.post(`${API_URL}/api/bars/${currentBar.id}/scores`, scoreData);
      }

      setSuccess(true);
      
      // Refresh the bar data
      if (onScoreSubmitted) {
        await onScoreSubmitted();
      }

      // Close dialog after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        if (onClose) {
          onClose();
        }
        // Reset score to par for next time
        setScore(currentBar.par ? currentBar.par.toString() : '');
      }, 2000);
    } catch (error) {
      console.error('Error submitting score:', error);
      setError(error.response?.data?.detail || 'Failed to submit score. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  if (!currentBar || !open) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <SportsGolfIcon color="primary" />
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {existingScore ? 'Update Your Score' : 'Log Your Score'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Current Bar Information */}
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(102, 126, 234, 0.1)', borderRadius: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
          {currentBar.name}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {currentBar.address}
          </Typography>
          <Chip
            label={`Par ${currentBar.par}`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
          {currentBar.drink && (
            <Chip
              icon={<LocalBarIcon />}
              label={currentBar.drink}
              size="small"
              sx={{
                backgroundColor: 'primary.dark',
                color: 'white',
              }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {existingScore && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You already have a score of {existingScore.score} for this bar. Update it below.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Score {existingScore ? 'updated' : 'submitted'} successfully!
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Box display="flex" gap={2} alignItems="flex-start">
          <TextField
            type="number"
            label="Your Score"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            inputProps={{ min: 1 }}
            required
            sx={{ flex: 1 }}
            helperText={`Par for this bar is ${currentBar.par}`}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !score}
            sx={{ mt: 1 }}
          >
            {loading ? 'Submitting...' : existingScore ? 'Update' : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default SubmitScore;

